import React, { useMemo } from 'react';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { SectionSummary } from '@/components/SectionSummary/SectionSummary';
import { MappedRepairHistoryItem } from '@/api/mappers/repair-history-section.mapper';
import { CarHistoryTimeline, CarHistoryEvent, CarfixEvent, TimelineItemCarfix } from '@/components/CarHistoryTimeline';
import { PhotoGallery } from '@/components/PhotoGallery';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';
import CardSectionWide from '@/components/CardSectionWideRate/CardSectionWide';
import { Verdict } from '@/helpers/ratingState';

interface RaportRepairHistoryProps {
  /** Lista pozycji historii napraw */
  historyItems: MappedRepairHistoryItem[];
  /** Podsumowanie rzeczoznawcy dotyczące historii napraw */
  expertSummary: string | null;
  /** Galeria zdjęć dokumentacji/historii napraw */
  galleryPhotos?: PhotoGalleryItem[];
}

/**
 * Konwertuje datę z formatu DD/MM/YYYY na YYYY-MM-DD
 */
const convertDateFormat = (date: string | null): string => {
  if (!date) {
    return new Date().toISOString().split('T')[0];
  }

  // Format DD/MM/YYYY -> YYYY-MM-DD
  const parts = date.split('/');
  if (parts.length === 3) {
    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return date;
};

/**
 * Mapuje MappedRepairHistoryItem na CarHistoryEvent (do infrastruktury timeline)
 */
const mapRepairItemToEvent = (
  item: MappedRepairHistoryItem,
  index: number
): CarHistoryEvent => {
  return {
    id: `repair-${index}-${item.repairDate || 'unknown'}`,
    date: item.repairDate ? convertDateFormat(item.repairDate) : new Date().toISOString().split('T')[0],
    type: 'other',
    title: item.repairedCarElement || 'Naprawa',
  };
};

/**
 * Mapuje MappedRepairHistoryItem na CarfixEvent (do wizualnego renderowania)
 */
const mapRepairItemToCarfix = (
  item: MappedRepairHistoryItem,
  index: number
): CarfixEvent => {
  const dateUnknown = !item.repairDate;
  const mileage = item.carMileage ? parseInt(item.carMileage.replace(/\s/g, ''), 10) : undefined;

  return {
    id: `repair-${index}-${item.repairDate || 'unknown'}`,
    date: item.repairDate || undefined,
    dateUnknown,
    place: item.repairPlace || undefined,
    placeUnknown: !item.repairPlace,
    mileage: mileage && !isNaN(mileage) ? mileage : undefined,
    title: item.repairedCarElement || 'Naprawa',
    description: item.repairedCarElement || undefined,
  };
};

export default function RaportRepairHistory({
  historyItems,
  expertSummary,
  galleryPhotos,
}: RaportRepairHistoryProps) {
  const hasItems = historyItems.length > 0;

  // Mapujemy dane historii napraw na format CarHistoryEvent (infrastruktura timeline)
  const timelineEvents: CarHistoryEvent[] = useMemo(() => {
    return historyItems.map((item, index) => mapRepairItemToEvent(item, index));
  }, [historyItems]);

  // Lookup CarfixEvent po id (wizualne renderowanie)
  const carfixLookup = useMemo(() => {
    const map = new Map<string, CarfixEvent>();
    historyItems.forEach((item, index) => {
      const carfix = mapRepairItemToCarfix(item, index);
      map.set(carfix.id, carfix);
    });
    return map;
  }, [historyItems]);

  return (
    <div className="mt-[56px] w-full">

      {/* Timeline historii napraw */}
      {hasItems ? (<div id="historia-napraw-lista">
        <SectionSubtitle
          title="Historia napraw"
          description=""
        />
        <div className="mt-11 pl-4 md:pl-0">
          <CarHistoryTimeline
            events={timelineEvents}
            showNoDataGaps={false}
            showYearMarkers={false}
            renderItem={({ event }) => {
              const carfix = carfixLookup.get(event.id);
              if (!carfix) return null;
              return <TimelineItemCarfix event={carfix} />;
            }}
          />
        </div>
      </div>
      ) : (
        <CardSectionWide verdict={Verdict.NEUTRAL} badgeCustomText="Brak" title="Historia napraw" description="Podczas ekspertyzy pojazdu nie znaleziono udokumentowanych informacji o przeprowadzonych naprawach ani badaniach technicznych." />
      )}

      {/* Podsumowanie rzeczoznawcy */}
      {expertSummary && (
        <div className="mt-20">
          <SectionSummary
            iconName="wrench-tool"
            title="Podsumowanie Rzeczoznawcy dotyczące historii napraw"
            content={expertSummary}
          />
        </div>
      )}

      {/* Galeria dokumentacji/historii napraw */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-historii-napraw" className="mt-12">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria historii napraw"
            iconName="wrench-tool"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}
    </div>
  );
}
